import { Money } from '../../components/ui/Money';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Building2, Briefcase, Calendar, Globe, Phone, Mail, Shield, Users, Heart, Plus, Pencil, X, MapPin, Droplets, Save, Linkedin } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const InfoRow = ({ icon: Icon, label, value, iconColor = 'text-gray-400' }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
    <Icon className={`h-4 w-4 mt-0.5 ${iconColor} flex-shrink-0`} />
    <div className="flex-1 min-w-0">
      <p className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</p>
      <p className="text-sm text-gray-800 mt-0.5">{value || '\u2014'}</p>
    </div>
  </div>
);

const RELATIONS = ['Spouse', 'Child', 'Parent', 'Sibling', 'Other'];

const PortalProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDepForm, setShowDepForm] = useState(false);
  const [editingDep, setEditingDep] = useState(null);
  const [editPassport, setEditPassport] = useState(false);
  const [passportNum, setPassportNum] = useState('');
  const [passportExpiry, setPassportExpiry] = useState('');
  const [savingPassport, setSavingPassport] = useState(false);
  const [editPersonal, setEditPersonal] = useState(false);
  const [personalForm, setPersonalForm] = useState({});
  const [savingPersonal, setSavingPersonal] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');

  const loadProfile = async () => {
    try {
      const res = await axios.get(`${API}/api/employee-portal/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('portal_token');
        navigate('/employee-portal/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProfile(); }, [token, navigate]);

  const handleSavePassport = async () => {
    setSavingPassport(true);
    try {
      await axios.put(`${API}/api/employee-portal/update-passport`, {
        passport_number: passportNum,
        passport_expiry: passportExpiry,
      }, { headers: { Authorization: `Bearer ${token}` } });
      await loadProfile();
      setEditPassport(false);
    } catch (err) { console.error(err); }
    setSavingPassport(false);
  };

  const startPassportEdit = () => {
    setPassportNum(profile?.passport_number || '');
    setPassportExpiry(profile?.passport_expiry || '');
    setEditPassport(true);
  };

  const startPersonalEdit = () => {
    setPersonalForm({
      date_of_birth: profile?.date_of_birth || '',
      blood_group: profile?.blood_group || '',
      gender: profile?.gender || '',
      marital_status: profile?.marital_status || '',
      nationality: profile?.nationality || '',
      father_name: profile?.father_name || '',
      mother_name: profile?.mother_name || '',
      spouse_name: profile?.spouse_name || '',
      personal_email: profile?.personal_email || '',
      mobile_no: profile?.mobile_no || '',
      address_1: profile?.address_1 || '',
      address_2: profile?.address_2 || '',
      city: profile?.city || '',
      state: profile?.state || '',
      country: profile?.country || '',
      zip_code: profile?.zip_code || '',
      driver_license: profile?.driver_license || '',
      hobbies: profile?.hobbies || '',
      linkedin: profile?.linkedin || '',
    });
    setEditPersonal(true);
  };

  const handleSavePersonal = async () => {
    setSavingPersonal(true);
    try {
      await axios.patch(`${API}/api/employee-portal/update-personal-details`, personalForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadProfile();
      setEditPersonal(false);
    } catch (err) { console.error(err); }
    setSavingPersonal(false);
  };

  const setP = (key, val) => setPersonalForm(p => ({ ...p, [key]: val }));

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600" />
      </div>
    );
  }

  if (!profile) return null;

  const dependents = profile.dependents || [];

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="portal-profile">
      {/* Profile Header */}
      <div className="text-center pb-2">
        <div className="w-20 h-20 rounded-full mx-auto mb-3 border-4 border-sky-100 overflow-hidden bg-sky-50 flex items-center justify-center">
          {profile.photo_url ? (
            <img src={`${API}${profile.photo_url}`} alt="" className="w-full h-full object-cover" />
          ) : (
            <User className="h-10 w-10 text-sky-300" />
          )}
        </div>
        <h2 className="text-lg font-bold text-gray-900">{profile.name}</h2>
        <p className="text-sm text-gray-500">{profile.position}</p>
        <div className="flex items-center justify-center gap-2 mt-2">
          <Badge variant="outline" className="text-xs">{profile.employee_code}</Badge>
          <Badge
            className={`text-xs ${
              profile.employment_status === 'Active' ? 'bg-emerald-100 text-emerald-700' :
              profile.employment_status === 'Suspended' ? 'bg-amber-100 text-amber-700' :
              'bg-red-100 text-red-700'
            }`}
          >
            {profile.employment_status}
          </Badge>
        </div>
      </div>

      {/* Employment Info */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Employment</h3>
          <InfoRow icon={Building2} label="Department" value={profile.department} iconColor="text-sky-400" />
          <InfoRow icon={Briefcase} label="Position" value={profile.position} iconColor="text-purple-400" />
          <InfoRow icon={Shield} label="Role" value={profile.employee_role} iconColor="text-blue-400" />
          <InfoRow icon={Calendar} label="Join Date" value={profile.join_date ? new Date(profile.join_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '\u2014'} iconColor="text-green-400" />
          <InfoRow icon={Building2} label="Visa Company" value={profile.visa_company} iconColor="text-teal-400" />
        </CardContent>
      </Card>

      {/* Personal Details (Editable by Employee) */}
      <Card className="border-0 shadow-sm" data-testid="personal-details-section">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Personal Details</h3>
            {!editPersonal ? (
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={startPersonalEdit} data-testid="edit-personal-btn">
                <Pencil className="h-3 w-3" /> Edit
              </Button>
            ) : (
              <div className="flex gap-1">
                <Button size="sm" className="h-7 text-xs bg-sky-600 hover:bg-sky-700 gap-1" onClick={handleSavePersonal} disabled={savingPersonal} data-testid="save-personal-btn">
                  <Save className="h-3 w-3" /> {savingPersonal ? '...' : 'Save'}
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditPersonal(false)}>Cancel</Button>
              </div>
            )}
          </div>

          {editPersonal ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Date of Birth</Label>
                  <Input type="date" value={personalForm.date_of_birth} onChange={e => setP('date_of_birth', e.target.value)} className="mt-1 h-9 text-sm" data-testid="personal-dob" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Blood Group</Label>
                  <Select value={personalForm.blood_group} onValueChange={v => setP('blood_group', v)}>
                    <SelectTrigger className="mt-1 h-9" data-testid="personal-blood"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Gender</Label>
                  <Select value={personalForm.gender} onValueChange={v => setP('gender', v)}>
                    <SelectTrigger className="mt-1 h-9" data-testid="personal-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Marital Status</Label>
                  <Select value={personalForm.marital_status} onValueChange={v => setP('marital_status', v)}>
                    <SelectTrigger className="mt-1 h-9" data-testid="personal-marital"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Single">Single</SelectItem>
                      <SelectItem value="Married">Married</SelectItem>
                      <SelectItem value="Divorced">Divorced</SelectItem>
                      <SelectItem value="Widowed">Widowed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Nationality</Label>
                <Input value={personalForm.nationality} onChange={e => setP('nationality', e.target.value)} className="mt-1 h-9 text-sm" data-testid="personal-nationality" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Father's Name</Label>
                  <Input value={personalForm.father_name} onChange={e => setP('father_name', e.target.value)} className="mt-1 h-9 text-sm" data-testid="personal-father" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Mother's Name</Label>
                  <Input value={personalForm.mother_name} onChange={e => setP('mother_name', e.target.value)} className="mt-1 h-9 text-sm" data-testid="personal-mother" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Spouse Name</Label>
                <Input value={personalForm.spouse_name} onChange={e => setP('spouse_name', e.target.value)} className="mt-1 h-9 text-sm" data-testid="personal-spouse" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Mobile No.</Label>
                  <Input value={personalForm.mobile_no} onChange={e => setP('mobile_no', e.target.value)} className="mt-1 h-9 text-sm" data-testid="personal-mobile" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Personal Email</Label>
                  <Input type="email" value={personalForm.personal_email} onChange={e => setP('personal_email', e.target.value)} className="mt-1 h-9 text-sm" data-testid="personal-email" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Address Line 1</Label>
                <Input value={personalForm.address_1} onChange={e => setP('address_1', e.target.value)} className="mt-1 h-9 text-sm" data-testid="personal-addr1" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Address Line 2</Label>
                <Input value={personalForm.address_2} onChange={e => setP('address_2', e.target.value)} className="mt-1 h-9 text-sm" data-testid="personal-addr2" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">City</Label>
                  <Input value={personalForm.city} onChange={e => setP('city', e.target.value)} className="mt-1 h-9 text-sm" data-testid="personal-city" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">State</Label>
                  <Input value={personalForm.state} onChange={e => setP('state', e.target.value)} className="mt-1 h-9 text-sm" data-testid="personal-state" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Country</Label>
                  <Input value={personalForm.country} onChange={e => setP('country', e.target.value)} className="mt-1 h-9 text-sm" data-testid="personal-country" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Zip / Postal Code</Label>
                  <Input value={personalForm.zip_code} onChange={e => setP('zip_code', e.target.value)} className="mt-1 h-9 text-sm" data-testid="personal-zip" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Driver License</Label>
                  <Input value={personalForm.driver_license} onChange={e => setP('driver_license', e.target.value)} className="mt-1 h-9 text-sm" data-testid="personal-license" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Hobbies</Label>
                  <Input value={personalForm.hobbies} onChange={e => setP('hobbies', e.target.value)} className="mt-1 h-9 text-sm" data-testid="personal-hobbies" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500">LinkedIn</Label>
                <Input value={personalForm.linkedin} onChange={e => setP('linkedin', e.target.value)} className="mt-1 h-9 text-sm" placeholder="https://linkedin.com/in/..." data-testid="personal-linkedin" />
              </div>
            </div>
          ) : (
            <>
              <InfoRow icon={Calendar} label="Date of Birth" value={profile.date_of_birth ? new Date(profile.date_of_birth + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null} iconColor="text-orange-400" />
              <InfoRow icon={Droplets} label="Blood Group" value={profile.blood_group} iconColor="text-red-400" />
              <InfoRow icon={User} label="Gender" value={profile.gender} iconColor="text-pink-400" />
              <InfoRow icon={Heart} label="Marital Status" value={profile.marital_status} iconColor="text-rose-400" />
              <InfoRow icon={Globe} label="Nationality" value={profile.nationality} iconColor="text-cyan-400" />
              <InfoRow icon={User} label="Father's Name" value={profile.father_name} iconColor="text-gray-400" />
              <InfoRow icon={User} label="Mother's Name" value={profile.mother_name} iconColor="text-gray-400" />
              <InfoRow icon={User} label="Spouse Name" value={profile.spouse_name} iconColor="text-gray-400" />
              <InfoRow icon={Phone} label="Mobile No." value={profile.mobile_no} iconColor="text-green-400" />
              <InfoRow icon={Mail} label="Personal Email" value={profile.personal_email} iconColor="text-red-400" />
              <InfoRow icon={MapPin} label="Address" value={[profile.address_1, profile.address_2, profile.city, profile.state, profile.country, profile.zip_code].filter(Boolean).join(', ') || null} iconColor="text-indigo-400" />
              <InfoRow icon={Shield} label="Driver License" value={profile.driver_license} iconColor="text-amber-400" />
              <InfoRow icon={Linkedin} label="LinkedIn" value={profile.linkedin} iconColor="text-blue-500" />
            </>
          )}
        </CardContent>
      </Card>

      {/* Reporting Managers */}
      {profile.reporting_managers?.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Reporting To</h3>
            <div className="space-y-2">
              {profile.reporting_managers.map((mgr, i) => (
                <div key={i} className="flex items-center gap-3 py-1">
                  <Users className="h-4 w-4 text-sky-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{mgr.name}</p>
                    <p className="text-[10px] text-gray-400">{mgr.position}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Passport & Documents */}
      <Card className="border-0 shadow-sm" data-testid="passport-section">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Passport Details</h3>
            {!editPassport && (
              <Button
                size="sm" variant="outline" className="h-7 text-xs gap-1"
                onClick={startPassportEdit}
                data-testid="edit-passport-btn"
              >
                <Pencil className="h-3 w-3" /> Edit
              </Button>
            )}
          </div>
          {editPassport ? (
            <div className="space-y-3">
              <div>
                <Label className="text-sm">Passport Number</Label>
                <Input value={passportNum} onChange={e => setPassportNum(e.target.value)} className="mt-1" data-testid="passport-number-input" />
              </div>
              <div>
                <Label className="text-sm">Passport Expiry</Label>
                <Input type="date" value={passportExpiry} onChange={e => setPassportExpiry(e.target.value)} className="mt-1" data-testid="passport-expiry-input" />
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={handleSavePassport} disabled={savingPassport} data-testid="save-passport-btn">
                  {savingPassport ? 'Saving...' : 'Save'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditPassport(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <>
              <InfoRow icon={Globe} label="Passport Number" value={profile.passport_number} iconColor="text-cyan-400" />
              <InfoRow icon={Calendar} label="Passport Expiry" value={profile.passport_expiry ? new Date(profile.passport_expiry + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null} iconColor="text-orange-400" />
            </>
          )}
        </CardContent>
      </Card>

      {/* Other Documents */}
      {profile.documents && Object.keys(profile.documents).length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Other Documents</h3>
            {Object.entries(profile.documents).map(([docType, docData]) => {
              if (!docData || typeof docData !== 'object') return null;
              const expiryDate = docData.expiry_date || docData.expiry;
              const number = docData.number || docData.document_number;
              if (!number && !expiryDate) return null;
              return (
                <div key={docType} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="text-sm text-gray-800 capitalize">{docType.replace(/_/g, ' ')}</p>
                    {number && <p className="text-[10px] text-gray-400">{number}</p>}
                  </div>
                  {expiryDate && (
                    <Badge variant="outline" className="text-[10px]">
                      Exp: {new Date(expiryDate + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </Badge>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Dependants Section */}
      <Card className="border-0 shadow-sm" data-testid="dependants-section">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Heart className="h-4 w-4 text-rose-400" />
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Dependants ({dependents.length})</h3>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs gap-1"
              onClick={() => { setEditingDep(null); setShowDepForm(true); }}
              data-testid="add-dependant-btn"
            >
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>

          {dependents.length === 0 ? (
            <div className="text-center py-6">
              <Heart className="h-8 w-8 mx-auto mb-2 text-gray-200" />
              <p className="text-xs text-gray-400">No dependants added yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {dependents.map(dep => {
                const expBadge = (dateStr) => {
                  if (!dateStr) return null;
                  const d = new Date(dateStr);
                  const now = new Date();
                  const diff = Math.floor((d - now) / (1000 * 60 * 60 * 24));
                  if (diff < 0) return <Badge className="bg-red-100 text-red-700 text-[8px] px-1 py-0">Expired</Badge>;
                  if (diff < 30) return <Badge className="bg-amber-100 text-amber-700 text-[8px] px-1 py-0">{diff}d left</Badge>;
                  if (diff < 90) return <Badge className="bg-yellow-100 text-yellow-700 text-[8px] px-1 py-0">{diff}d</Badge>;
                  return null;
                };

                return (
                  <div key={dep.id} className="border rounded-xl p-3 bg-gray-50/50" data-testid={`dependant-${dep.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{dep.name}</p>
                        <Badge className="bg-sky-50 text-sky-600 text-[9px] px-1.5 py-0 mt-0.5 border-sky-200">{dep.relation}</Badge>
                      </div>
                      <button
                        onClick={() => { setEditingDep(dep); setShowDepForm(true); }}
                        className="p-1.5 rounded-lg hover:bg-gray-200 transition"
                        data-testid={`edit-dependant-${dep.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5 text-gray-400" />
                      </button>
                    </div>
                    <div className="mt-2 space-y-1 text-[11px] text-gray-500">
                      {dep.passport_number && (
                        <div className="flex items-center justify-between">
                          <span>Passport: {dep.passport_number}</span>
                          {expBadge(dep.passport_expiry)}
                        </div>
                      )}
                      {dep.emirates_id && (
                        <div className="flex items-center justify-between">
                          <span>Emirates ID: {dep.emirates_id}</span>
                          {expBadge(dep.emirates_id_expiry)}
                        </div>
                      )}
                      {dep.phone && <p>Phone: {dep.phone}</p>}
                      {dep.insurance_sponsored && (
                        <Badge className="bg-emerald-50 text-emerald-600 text-[9px] px-1.5 py-0 border-emerald-200">
                          Insurance: <Money value={dep.insurance_cost} />/yr
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dependant Form Dialog */}
      {showDepForm && (
        <DependantFormDialog
          token={token}
          dep={editingDep}
          onClose={() => { setShowDepForm(false); setEditingDep(null); }}
          onSaved={() => { setShowDepForm(false); setEditingDep(null); loadProfile(); }}
        />
      )}
    </div>
  );
};

/* ── Dependant Form Dialog ─────────────────────────────────── */
const DependantFormDialog = ({ token, dep, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: dep?.name || '',
    relation: dep?.relation || '',
    passport_number: dep?.passport_number || '',
    passport_expiry: dep?.passport_expiry || '',
    emirates_id: dep?.emirates_id || '',
    emirates_id_expiry: dep?.emirates_id_expiry || '',
    phone: dep?.phone || '',
    insurance_sponsored: dep?.insurance_sponsored || false,
    insurance_cost: dep?.insurance_cost || 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isEdit = !!dep;

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.relation) {
      setError('Name and Relation are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (isEdit) {
        await axios.put(`${API}/api/employee-portal/dependants/${dep.id}`, form, { headers });
      } else {
        await axios.post(`${API}/api/employee-portal/dependants`, form, { headers });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="dependant-form-dialog">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto z-10">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h3 className="text-base font-bold text-gray-900">{isEdit ? 'Edit' : 'Add'} Dependant</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100"><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSave} className="p-5 space-y-3">
          <div>
            <Label className="text-sm">Name *</Label>
            <Input value={form.name} onChange={e => set('name', e.target.value)} className="mt-1" data-testid="dep-name" />
          </div>
          <div>
            <Label className="text-sm">Relation *</Label>
            <Select value={form.relation} onValueChange={v => set('relation', v)}>
              <SelectTrigger className="mt-1" data-testid="dep-relation">
                <SelectValue placeholder="Select relation" />
              </SelectTrigger>
              <SelectContent>
                {RELATIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Passport No.</Label>
              <Input value={form.passport_number} onChange={e => set('passport_number', e.target.value)} className="mt-1" data-testid="dep-passport" />
            </div>
            <div>
              <Label className="text-sm">Passport Expiry</Label>
              <Input type="date" value={form.passport_expiry} onChange={e => set('passport_expiry', e.target.value)} className="mt-1" data-testid="dep-passport-exp" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Emirates ID</Label>
              <Input value={form.emirates_id} onChange={e => set('emirates_id', e.target.value)} className="mt-1" data-testid="dep-eid" />
            </div>
            <div>
              <Label className="text-sm">EID Expiry</Label>
              <Input type="date" value={form.emirates_id_expiry} onChange={e => set('emirates_id_expiry', e.target.value)} className="mt-1" data-testid="dep-eid-exp" />
            </div>
          </div>
          <div>
            <Label className="text-sm">Phone</Label>
            <Input value={form.phone} onChange={e => set('phone', e.target.value)} className="mt-1" data-testid="dep-phone" />
          </div>
          <div className="flex items-center gap-3 py-2">
            <input
              type="checkbox"
              checked={form.insurance_sponsored}
              onChange={e => set('insurance_sponsored', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
              data-testid="dep-insurance-check"
            />
            <Label className="text-sm">Company sponsors insurance</Label>
          </div>
          {form.insurance_sponsored && (
            <div>
              <Label className="text-sm">Annual Insurance Cost (AED)</Label>
              <Input type="number" value={form.insurance_cost} onChange={e => set('insurance_cost', parseFloat(e.target.value) || 0)} className="mt-1" data-testid="dep-insurance-cost" />
            </div>
          )}
          {error && <p className="text-sm text-red-500 text-center">{error}</p>}
          <Button type="submit" className="w-full py-4 bg-sky-600 hover:bg-sky-700" disabled={saving} data-testid="dep-save-btn">
            {saving ? 'Saving...' : isEdit ? 'Update Dependant' : 'Add Dependant'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PortalProfile;
